<template>
  <div class="builder-group">
    <div class="builder-group-header">
      <el-button size="small" @click="addRule">ルールを追加</el-button>
    </div>
    <div
      class="builder-rules"
      v-for="(rule, idx) in props.group.rules"
      :key="idx"
    >
      <div class="builder-rule">
        <el-form-item
          :prop="`${formPrefix}.rules.${idx}.fieldName`"
          :rules="fieldNameRules(rule)"
        >
          <el-select
            v-model="rule.fieldName"
            filterable
            allow-create
            default-first-option
            placeholder="フィールド名"
            size="small"
            style="width: 180px"
            @change="onFieldNameChange(rule)"
            @blur="onFieldNameBlur(rule)"
          >
            <el-option
              v-for="opt in fieldOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          :prop="`${formPrefix}.rules.${idx}.type`"
          :rules="[
            {
              required: true,
              message: 'タイプは必須です'
            }
          ]"
        >
          <el-input
            v-if="isPresetOrderAutoMapField(rule.fieldName)"
            :model-value="getConditionTypeLabel(rule.type)"
            readonly
            size="small"
            style="width: 120px"
          />
          <el-select
            v-else
            v-model="rule.type"
            placeholder="タイプ"
            size="small"
            style="width: 120px"
            @change="onTypeChange(rule)"
          >
            <el-option
              v-for="opt in conditionTypeOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          :prop="`${formPrefix}.rules.${idx}.condition`"
          :rules="[
            {
              required: true,
              message: '条件は必須です'
            }
          ]"
        >
          <el-select
            v-model="rule.condition"
            placeholder="条件"
            size="small"
            style="width: 120px"
          >
            <el-option
              v-for="opt in getOperatorOptions(rule.type)"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          :prop="`${formPrefix}.rules.${idx}.value`"
          :rules="valueRules(rule)"
        >
          <el-select
            v-if="isHarnessCutRule(rule)"
            v-model="rule.value"
            placeholder="値"
            size="small"
            class="value-field"
          >
            <el-option
              v-for="opt in harnessCutValueOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-select
            v-else-if="isIgnitionTypeRule(rule)"
            v-model="rule.value"
            placeholder="値"
            size="small"
            class="value-field"
          >
            <el-option
              v-for="opt in ignitionTypeValueOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-select
            v-else-if="isServiceCodeRule(rule)"
            v-model="rule.value"
            placeholder="値"
            size="small"
            class="value-field"
          >
            <el-option
              v-for="opt in serviceCodeValueOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-select
            v-else-if="isBusinessTypeRule(rule)"
            v-model="rule.value"
            placeholder="値"
            size="small"
            class="value-field"
          >
            <el-option
              v-for="opt in businessTypeValueOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-select
            v-else-if="rule.type === 'boolean'"
            v-model="rule.value"
            placeholder="値"
            size="small"
            class="value-field"
          >
            <el-option
              v-for="opt in booleanValueOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-input
            v-else-if="rule.type === 'number'"
            v-model="rule.value"
            type="text"
            inputmode="decimal"
            placeholder="値"
            size="small"
            class="value-field"
            @blur="onNumberValueBlur(rule)"
          />
          <el-input
            v-else
            v-model="rule.value"
            type="text"
            placeholder="値"
            size="small"
            class="value-field"
            @blur="onTextValueBlur(rule)"
          />
        </el-form-item>
        <el-button
          type="danger"
          size="small"
          plain
          circle
          style="margin-left: auto"
          v-if="props.group.rules.length > 1"
          @click="deleteRule(idx)"
        >
          <el-icon><DeleteFilled /></el-icon>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import { DeleteFilled } from '@element-plus/icons-vue';
  import type {
    ConditionGroup,
    ConditionRule,
    ConditionValueType
  } from '@/types/master/orderAutoMap';
  import type { BusinessTypesDataState } from '@/types/master/businessType';
  import {
    booleanValueOptions,
    buildBusinessTypeValueOptions,
    conditionOperatorOptions,
    conditionTypeOptions,
    createEmptyConditionRule,
    harnessCutValueOptions,
    ignitionTypeValueOptions,
    isBusinessTypeRule,
    isHarnessCutRule,
    isIgnitionTypeRule,
    isServiceCodeRule,
    isValidConditionFieldName,
    isPresetOrderAutoMapField,
    getConditionTypeLabel,
    getPresetFieldDefaultType,
    orderAutoMapFieldOptions,
    serviceCodeValueOptions
  } from '@/libs/orderAutoMap';
  import {
    isValidSignedDecimal,
    normalizeSignedDecimal
  } from '@/libs/restriction';
  import type { FormItemRule } from 'element-plus';

  const props = defineProps<{
    group: ConditionGroup;
    formPrefix?: string;
  }>();

  const emit = defineEmits<{
    'update:group': [group: ConditionGroup];
  }>();

  const store = useStore();

  onMounted(async () => {
    if (!store.state.businessTypesMaster.listData.length) {
      await store.dispatch('businessTypesMaster/fetch');
    }
  });

  const businessTypeValueOptions = computed(() =>
    buildBusinessTypeValueOptions(
      store.state.businessTypesMaster.listData as BusinessTypesDataState[]
    )
  );

  const fieldOptions = computed(() => {
    const presetValues = new Set(
      orderAutoMapFieldOptions.map((opt) => opt.value)
    );
    const customFields = [
      ...new Set(
        props.group.rules
          .map((rule) => rule.fieldName)
          .filter((name) => name && !presetValues.has(name))
      )
    ];
    return [
      ...orderAutoMapFieldOptions,
      ...customFields.map((value) => ({ label: value, value }))
    ];
  });

  const getOperatorOptions = (type: ConditionValueType) =>
    conditionOperatorOptions[type] ?? conditionOperatorOptions.string;

  const fieldNameRules = (rule: ConditionRule): FormItemRule[] => [
    {
      validator: (_formRule, value: string, callback) => {
        const trimmed = value.trim();
        if (trimmed !== value) {
          rule.fieldName = trimmed;
        }
        if (!trimmed) {
          callback(new Error('フィールド名は必須です'));
          return;
        }
        if (!isValidConditionFieldName(trimmed)) {
          callback(
            new Error(
              'フィールド名はcamelCase（先頭小文字の英数字）で入力してください'
            )
          );
          return;
        }
        callback();
      },
      trigger: ['change', 'blur']
    }
  ];

  const valueRules = (rule: ConditionRule): FormItemRule[] => [
    {
      validator: (_formRule, value: string, callback) => {
        const trimmed = value.trim();
        if (!trimmed) {
          callback(new Error('値は必須です'));
          return;
        }
        if (rule.type === 'number' && !isValidSignedDecimal(trimmed)) {
          callback(new Error('値は数値で入力してください'));
          return;
        }
        callback();
      },
      trigger: ['change', 'blur']
    }
  ];

  const applyPresetFieldDefaults = (rule: ConditionRule) => {
    const defaultType = getPresetFieldDefaultType(rule.fieldName);
    if (!defaultType) return false;

    rule.type = defaultType;

    if (isHarnessCutRule(rule)) {
      rule.condition = 'equal';
      if (rule.value !== 'true' && rule.value !== 'false') {
        rule.value = 'false';
      }
      return true;
    }
    if (isIgnitionTypeRule(rule)) {
      rule.condition = 'equal';
      if (!ignitionTypeValueOptions.some((opt) => opt.value === rule.value)) {
        rule.value = ignitionTypeValueOptions[0]?.value ?? '1';
      }
      return true;
    }
    if (isServiceCodeRule(rule)) {
      rule.condition = 'equal';
      if (!serviceCodeValueOptions.some((opt) => opt.value === rule.value)) {
        rule.value = serviceCodeValueOptions[0]?.value ?? 'BQ';
      }
      return true;
    }
    if (isBusinessTypeRule(rule)) {
      rule.condition = 'equal';
      if (
        !businessTypeValueOptions.value.some((opt) => opt.value === rule.value)
      ) {
        rule.value = businessTypeValueOptions.value[0]?.value ?? '1';
      }
      return true;
    }

    const operators = getOperatorOptions(defaultType);
    if (!operators.some((opt) => opt.value === rule.condition)) {
      rule.condition = operators[0]?.value ?? 'equal';
    }
    if (defaultType === 'number' && Number.isNaN(Number(rule.value))) {
      rule.value = '';
    }
    return true;
  };

  const trimFieldName = (rule: ConditionRule) => {
    const trimmed = rule.fieldName.trim();
    if (trimmed === rule.fieldName) return false;
    rule.fieldName = trimmed;
    return true;
  };

  const onFieldNameChange = (rule: ConditionRule) => {
    rule.fieldName = rule.fieldName.trim();
    applyPresetFieldDefaults(rule);
  };

  const onFieldNameBlur = (rule: ConditionRule) => {
    if (trimFieldName(rule)) {
      applyPresetFieldDefaults(rule);
    }
  };

  const onNumberValueBlur = (rule: ConditionRule) => {
    rule.value = normalizeSignedDecimal(rule.value);
  };

  const onTextValueBlur = (rule: ConditionRule) => {
    rule.value = rule.value.trim();
  };

  const onTypeChange = (rule: ConditionRule) => {
    const operators = getOperatorOptions(rule.type);
    if (!operators.some((opt) => opt.value === rule.condition)) {
      rule.condition = operators[0]?.value ?? 'equal';
    }
    if (rule.type === 'boolean') {
      rule.value = isHarnessCutRule(rule) ? 'false' : 'true';
    } else if (rule.type === 'number' && isIgnitionTypeRule(rule)) {
      rule.value = ignitionTypeValueOptions[0]?.value ?? '1';
    } else if (rule.type === 'number' && isBusinessTypeRule(rule)) {
      rule.value = businessTypeValueOptions.value[0]?.value ?? '1';
    } else {
      rule.value = '';
    }
  };

  const addRule = () => {
    emit('update:group', {
      rules: [...props.group.rules, createEmptyConditionRule()]
    });
  };

  const deleteRule = (idx: number) => {
    if (props.group.rules.length <= 1) return;
    emit('update:group', {
      rules: props.group.rules.filter((_, i) => i !== idx)
    });
  };
</script>

<style scoped lang="scss">
  .builder-group {
    width: 100%;
    border: 1px solid #e0e0e0;
    padding: 12px;
    margin-bottom: 10px;
    background: #fafbfc;
  }
  .builder-group-header {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }
  .builder-rule {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    flex-wrap: nowrap;
  }
  .builder-rule :deep(.el-form-item) {
    margin-bottom: 0;
    flex-shrink: 0;
    align-self: flex-start;
  }
  .builder-rule :deep(.el-form-item.is-error) {
    margin-bottom: 18px;
  }
  .builder-rule :deep(.el-form-item__error) {
    white-space: nowrap;
  }
  .builder-rule :deep(.el-select),
  .builder-rule :deep(.el-input) {
    height: 32px;
  }
  .builder-rule :deep(.value-field) {
    width: 140px;
  }
</style>
